import { useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { BOOKING_DETAILS_VOUCHER_QUERY } from '../../graphql/bookingDetail'
import { lang } from '../../localization'
import { BookingDetail } from '../../types/bookingDetails'
import CreatVoucherEN from './EN'
import CreatVoucherFA from './FA'
import './index.css'

function stripTagsWithNewlines(html: string): string[] {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const paragraphs = doc.getElementsByTagName('p')
  const sentences: string[] = []
  let textForSplit = '.'
  if (lang === 'en') {
    textForSplit = '***'
  }

  // Extract sentences from each <p> element
  for (let i = 0; i < paragraphs.length; i++) {
    const paragraph = paragraphs[i]
    const paragraphText = paragraph.textContent?.trim()

    // Split paragraph into sentences based on period (.)
    if (paragraphText) {
      const paragraphSentences = paragraphText
        .split(textForSplit)
        .map((sentence) => sentence.trim())
      sentences.push(...paragraphSentences)
    }
  }

  // If no <p> tags found or no sentences extracted, try extracting directly from textContent
  if (sentences.length === 0) {
    const textContent = doc.body.textContent?.trim()
    if (textContent) {
      const textContentSentences = textContent
        .split(textForSplit)
        .map((sentence) => sentence.trim())
      sentences.push(...textContentSentences)
    }
  }

  return sentences
}

function splitTextByWords(inputString: string, maxLength: number): string[] {
  // Ensure the input is a string
  if (typeof inputString !== 'string') {
    return []
  }

  const segments: string[] = []
  let currentIndex = 0

  while (currentIndex < inputString.length) {
    let endIndex = currentIndex + maxLength
    if (endIndex >= inputString.length) {
      segments.push(inputString.substring(currentIndex))
      break
    }

    const lastSpaceIndex = inputString.lastIndexOf(' ', endIndex)
    if (lastSpaceIndex === -1 || lastSpaceIndex < currentIndex) {
      endIndex = currentIndex + maxLength // No spaces found, break at maxLength
    } else {
      endIndex = lastSpaceIndex // Break at the last space
    }

    segments.push(inputString.substring(currentIndex, endIndex))
    currentIndex = endIndex + 1 // Move past the space
  }

  return segments
}

export default function Voucher() {
  const params = useParams<{ hash: string }>()

  const { data, loading } = useQuery<{
    bookingDetailVoucher: BookingDetail
  }>(BOOKING_DETAILS_VOUCHER_QUERY, {
    variables: {
      hash: params.hash
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onError: (err) => {
      toast.error(err.message)
    }
  })

  return (
    <div className="root">
      {lang === 'en' ? (
        <CreatVoucherEN
          data={data?.bookingDetailVoucher}
          masterReservationNumber={params.hash}
          stripTagsWithNewlines={stripTagsWithNewlines}
          splitTextByWords={splitTextByWords}
          loading={loading}
        />
      ) : (
        <CreatVoucherFA
          data={data?.bookingDetailVoucher}
          masterReservationNumber={params.hash}
          stripTagsWithNewlines={stripTagsWithNewlines}
          splitTextByWords={splitTextByWords}
          loading={loading}
        />
      )}
    </div>
  )
}
